'use client';

import React from 'react';
import type { MontessoriFocus } from '@/lib/montessori';
import { BookText, Eye, Heart, Sparkles, Grid3x3 } from 'lucide-react';

interface MontessoriFocusSelectorProps {
  value: MontessoriFocus;
  onChange: (focus: MontessoriFocus) => void;
  disabled?: boolean;
}

const focusOptions: Array<{
  value: MontessoriFocus;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}> = [
  {
    value: 'mixed',
    label: 'Mixed (Recommended)',
    description: 'Balanced approach across all areas',
    icon: <Grid3x3 size={18} />,
    color: '#8BC34A',
  },
  {
    value: 'language',
    label: 'Language Development',
    description: 'Focus on vocabulary and communication',
    icon: <BookText size={18} />,
    color: '#03A9F4',
  },
  {
    value: 'sensorial',
    label: 'Sensorial Exploration',
    description: 'Engage textures, colors, and senses',
    icon: <Eye size={18} />,
    color: '#FF9800',
  },
  {
    value: 'emotional',
    label: 'Emotional Growth',
    description: 'Build empathy and self-awareness',
    icon: <Heart size={18} />,
    color: '#E91E63',
  },
  {
    value: 'practical',
    label: 'Practical Life Skills',
    description: 'Everyday activities and independence',
    icon: <Sparkles size={18} />,
    color: '#9C27B0',
  },
];

export default function MontessoriFocusSelector({
  value,
  onChange,
  disabled = false,
}: MontessoriFocusSelectorProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <label
        style={{
          fontSize: '0.9375rem',
          fontWeight: 600,
          color: '#2c3e50',
        }}
      >
        Learning Focus
      </label>
      
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '0.5rem',
        }}
        className="montessori-focus-grid"
      >
        {focusOptions.map((option) => {
          const isSelected = value === option.value;
          
          return (
            <button
              key={option.value}
              onClick={() => !disabled && onChange(option.value)}
              disabled={disabled}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 0.5rem',
                borderRadius: '12px',
                background: isSelected
                  ? `linear-gradient(135deg, ${option.color}15, ${option.color}10)`
                  : 'rgba(245, 247, 250, 0.5)',
                border: isSelected
                  ? `2px solid ${option.color}`
                  : '2px solid rgba(0, 0, 0, 0.1)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                textAlign: 'center',
              }}
              onMouseEnter={(e) => {
                if (!disabled && !isSelected) {
                  e.currentTarget.style.borderColor = `${option.color}60`;
                  e.currentTarget.style.background = `${option.color}08`;
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled && !isSelected) {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.background = 'rgba(245, 247, 250, 0.5)';
                }
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: isSelected ? option.color + '20' : 'transparent',
                  color: isSelected ? option.color : '#64748b',
                  transition: 'all 0.2s',
                }}
              >
                {option.icon}
              </div>
              <span style={{ 
                fontSize: '0.8125rem', 
                fontWeight: 600,
                color: isSelected ? option.color : '#2c3e50',
                lineHeight: '1.2',
              }}>
                {option.label}
              </span>
              <p
                style={{
                  margin: 0,
                  fontSize: '0.6875rem',
                  color: '#64748b',
                  lineHeight: '1.3',
                }}
              >
                {option.description}
              </p>
            </button>
          );
        })}
      </div>
      
      <style>{`
        @media (max-width: 1024px) {
          .montessori-focus-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        
        @media (max-width: 640px) {
          .montessori-focus-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
}

